import { motion } from 'framer-motion';
import { useState } from 'react';
import PageTransition from '../components/common/PageTransition';
import EditionCard from '../components/editions/EditionCard';
import PDFPreview from '../components/editions/PDFPreview';
import { editions } from '../data/editions';
import { FaWhatsapp, FaCheckCircle } from 'react-icons/fa';
import SEO from '../components/common/SEO';

const comparisonData = [
  { feature: 'Age Group', micro: '4–9 years', macro: '10–15 years' },
  { feature: 'Reading Level', micro: 'Beginner to Intermediate', macro: 'Intermediate to Advanced' },
  { feature: 'Story Length', micro: 'Short stories (200–400 words)', macro: 'Longer stories (600–1000 words)' },
  { feature: 'Puzzles', micro: 'Picture puzzles, mazes, coloring', macro: 'Logic puzzles, crosswords, sudoku' },
  { feature: 'Learning Focus', micro: 'Basic concepts, vocabulary', macro: 'Critical thinking, current events' },
  { feature: 'Activities', micro: 'Hands-on crafts, simple games', macro: 'Science experiments, debates' },
  { feature: 'Languages', micro: 'English + Telugu words', macro: 'English + Telugu' },
  { feature: 'Pages per Issue', micro: '4 pages', macro: '4 pages' },
];

const benefits = [
  { icon: '🎯', title: 'Age-Appropriate', description: 'Content carefully designed for specific age groups and learning levels', color: '#D32F2F' },
  { icon: '🧠', title: 'Educational', description: 'Aligned with school curriculum while making learning fun and engaging', color: '#1565C0' },
  { icon: '🌈', title: 'Colorful & Fun', description: 'Vibrant illustrations and interactive activities that kids love', color: '#7B2FBE' },
  { icon: '📅', title: 'Weekly Issues', description: 'Fresh content every week to keep children excited and engaged', color: '#3CB044' },
  { icon: '🏆', title: 'Contests & Prizes', description: 'Regular competitions with exciting rewards and certificates', color: '#F57C00' },
  { icon: '💯', title: 'Quality Content', description: 'Created by educators and child development experts', color: '#00897B' },
];

const Editions = () => {
  const [selectedPDF, setSelectedPDF] = useState(null);

  return (
    <PageTransition>
      <SEO
        title="Our Editions - Micro (Ages 4-9) & Macro (Ages 10-15)"
        description="Explore our age-appropriate editions: Junior Micro Edition (Ages 4-9) for foundational reading, and Senior Macro Edition (Ages 10-15) for advanced learning & world news."
        keywords="junior edition kids, senior edition teens, age group magazines, reading level children, kids weekly subscription"
      />
      <div className="min-h-screen">

        {/* Hero */}
        <section className="relative py-24 md:py-36 overflow-hidden"
          style={{ background: 'linear-gradient(160deg, #F3EEFF 0%, #EFF6FF 40%, #E8F5E9 100%)' }}>
          <div className="absolute inset-0 opacity-20 pointer-events-none"
            style={{ backgroundImage: 'radial-gradient(circle, rgba(123,47,190,0.12) 1px, transparent 1px)', backgroundSize: '36px 36px' }} />
          <div className="absolute top-0 right-0 w-96 h-96 opacity-20 pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(123,47,190,0.35), transparent 70%)', filter: 'blur(60px)', transform: 'translate(25%,-25%)' }} />

          {[{ e: '📚', x: '7%', y: '22%' }, { e: '🌈', x: '87%', y: '18%' }, { e: '🎨', x: '80%', y: '72%' }, { e: '⭐', x: '10%', y: '70%' }].map((item, i) => (
            <motion.div key={i} className="absolute text-5xl opacity-20 pointer-events-none select-none"
              style={{ left: item.x, top: item.y }}
              animate={{ y: [0, -20, 0], rotate: [0, 12, -12, 0] }}
              transition={{ duration: 4 + i * 0.4, repeat: Infinity, delay: i * 0.3 }}>
              {item.e}
            </motion.div>
          ))}

          <div className="container mx-auto px-4 relative z-10">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
                className="text-8xl mb-5">📖</motion.div>
              <h1 className="text-5xl md:text-7xl font-display font-bold mb-5 leading-tight" style={{ color: '#1A1F2E' }}>
                Our{' '}
                <span style={{ background: 'linear-gradient(135deg, #7B2FBE, #1565C0)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Editions
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-neutral-gray-dark leading-relaxed max-w-xl mx-auto">
                Perfectly crafted content for every age group! Choose the edition that's just right for your child 🌟
              </p>
            </motion.div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
            <svg viewBox="0 0 1440 80" className="w-full" preserveAspectRatio="none" style={{ display: 'block' }}>
              <path fill="white" d="M0,40L60,45C120,50,240,60,360,55C480,50,600,30,720,28C840,25,960,35,1080,40C1200,45,1320,42,1380,40L1440,38L1440,80L0,80Z" />
            </svg>
          </div>
        </section>

        {/* Edition Cards */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-7xl mx-auto">
              {editions.map((edition, index) => (
                <motion.div
                  key={edition.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15, duration: 0.6 }}
                  className="w-full"
                >
                  <EditionCard edition={edition} onReadSample={() => setSelectedPDF(edition)} />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-16 md:py-24" style={{ background: 'linear-gradient(160deg, #F0FFF4, #EFF6FF)' }}>
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-display font-bold mb-4" style={{ color: '#1A1F2E' }}>Compare Our Editions 📊</h2>
                <p className="text-lg text-neutral-gray-dark max-w-xl mx-auto">Find the perfect fit for your child's learning journey</p>
                <div className="mt-4 mx-auto w-24 h-1 rounded-full" style={{ background: 'linear-gradient(90deg, #7B2FBE, #1565C0)' }} />
              </div>

              <div className="bg-white rounded-2xl overflow-hidden shadow-card">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr style={{ background: 'linear-gradient(135deg, #7B2FBE, #1565C0)' }}>
                        <th className="px-6 py-4 text-left font-display font-bold text-white text-base">Features</th>
                        <th className="px-6 py-4 text-center font-display font-bold text-white text-base">🌈 Micro Edition</th>
                        <th className="px-6 py-4 text-center font-display font-bold text-white text-base">🚀 Macro Edition</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.map((row, index) => (
                        <motion.tr
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.05 }}
                          className="border-b border-gray-100 transition-colors"
                          style={{ background: index % 2 === 0 ? 'white' : '#FAFBFF' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#EFF6FF'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = index % 2 === 0 ? 'white' : '#FAFBFF'; }}
                        >
                          <td className="px-6 py-4 font-display font-semibold text-sm" style={{ color: '#1A1F2E' }}>{row.feature}</td>
                          <td className="px-6 py-4 text-center text-sm text-neutral-gray-dark">{row.micro}</td>
                          <td className="px-6 py-4 text-center text-sm text-neutral-gray-dark">{row.macro}</td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4" style={{ color: '#1A1F2E' }}>Why Choose The Tiny Times? 🌟</h2>
              <p className="text-lg text-neutral-gray-dark max-w-xl mx-auto">Everything that makes us India's favourite children's magazine</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08, duration: 0.5 }}
                  whileHover={{ y: -8, scale: 1.02 }}
                  className="rounded-2xl p-6 text-center"
                  style={{
                    background: `${benefit.color}08`,
                    border: `1px solid ${benefit.color}18`,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  }}
                >
                  <div className="text-6xl mb-4">{benefit.icon}</div>
                  <h3 className="text-xl font-display font-bold mb-3" style={{ color: benefit.color }}>{benefit.title}</h3>
                  <p className="text-sm text-neutral-gray-dark leading-relaxed">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20" style={{ background: 'linear-gradient(135deg, #1E7C24, #145218)' }}>
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-3xl mx-auto text-center">
              <div className="text-7xl mb-5">🎉</div>
              <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-5">Ready to Start Learning?</h2>
              <p className="text-xl text-white/85 mb-8">Subscribe today and give your child the gift of joyful learning!</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a href="https://wa.me/919618377750" target="_blank" rel="noopener noreferrer"
                  whileHover={{ scale: 1.05, y: -3 }} whileTap={{ scale: 0.96 }}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-button font-display font-bold text-xl text-white"
                  style={{ background: '#25D366', boxShadow: '0 6px 24px rgba(37,211,102,0.4)' }}>
                  <FaWhatsapp className="text-2xl" /> Subscribe Now 🚀
                </motion.a>
                <motion.a href="/samples" whileHover={{ scale: 1.05, y: -3 }} whileTap={{ scale: 0.96 }}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-button font-display font-bold text-xl"
                  style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '2px solid rgba(255,255,255,0.3)' }}>
                  View Samples 📖
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* PDF Preview Modal */}
        {selectedPDF && (
          <PDFPreview edition={selectedPDF} onClose={() => setSelectedPDF(null)} />
        )}
      </div>
    </PageTransition>
  );
};

export default Editions;